/**
 * Matchup view model — one game, one derived presentation model, many variants.
 *
 * Single source for every matchup surface's derived presentation fields
 * (sport label/accent, team initials, countdown seed, date line, CTA copy).
 * No surface may re-derive these independently — see
 * docs/SPRINT8_WI1_DESIGN_SPEC.md §5/§6.
 *
 * Pure: no fetch, no state, no mutation. The live countdown ticker lives in
 * the companion hook `useMatchupCountdown` (src/hooks/useMatchupCountdown.js).
 */
import {
  formatGameTime,
  formatSportLabel,
  formatStartsIn,
  getSportNeonStyle,
  getTeamInitials,
} from './gameTicketStyles';
import { getSportIconId, normalizeSportKey } from '../constants/sportTheme';

/** Canonical matchup CTA copy — one source of truth for every variant. */
export const MATCHUP_CTA = Object.freeze({
  primary: 'Lock It In',
  secondary: 'Send Ticket',
});

/**
 * @param {object} game - raw upcoming/live event ({ id, sport, a, b, startTime, scoreA, scoreB, status })
 * @returns derived presentation fields consumed by every matchup variant.
 */
export function buildMatchupViewModel(game) {
  const safe = game || {};
  const { date, time } = formatGameTime(safe.startTime);
  const accent = getSportNeonStyle(safe.sport);
  const sportKey = normalizeSportKey(safe.sport) || '';
  const sportIconId = getSportIconId(safe.sport);
  const hasScores = safe.scoreA != null && safe.scoreB != null;

  return {
    id: safe.id ?? null,
    sportKey,
    sportLabel: formatSportLabel(safe.sport),
    /** Sport category glyph id for SportIcon — from sportTheme (Sprint 12 WI-4). */
    sportIconId,
    /** Sport-accent class set: { border, glow, ring, badge, initials }. */
    accent,
    home: { name: safe.a || '—', initials: getTeamInitials(safe.a) },
    away: { name: safe.b || '—', initials: getTeamInitials(safe.b) },
    startTime: safe.startTime ?? null,
    /** Countdown seed; live updates come from useMatchupCountdown(startTime). */
    startsIn: formatStartsIn(safe.startTime),
    date,
    time,
    /** "Jun 28 · 7:30 PM" when a date exists, else just the time. */
    dateLine: date ? `${date} · ${time}` : time,
    scores: {
      a: hasScores ? safe.scoreA : null,
      b: hasScores ? safe.scoreB : null,
      hasScores,
    },
    status: safe.status ?? null,
    cta: MATCHUP_CTA,
    /** Original event — passed back to handlers so flows are unchanged. */
    game: safe,
  };
}

export default buildMatchupViewModel;
